import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
} from '@mui/material';
import {
  SupportAgent,
  HelpOutline,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const Support = () => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
        py: 8,
      }}
    >
      <Container maxWidth="lg">
        {/* Main Heading */}
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            mb: 6,
            color: '#1976d2',
            fontSize: { xs: '2rem', md: '2.5rem' },
          }}
        >
          Contact us
        </Typography>

        {/* Contact Cards */}
        <Grid container spacing={4}>
          {/* Sales Team Card */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: '100%',
                borderRadius: 3,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                },
              }}
            >
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                {/* Icon */}
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: '#FFEB3B',
                    border: '3px solid #1976d2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  <SupportAgent
                    sx={{
                      fontSize: 40,
                      color: '#1976d2',
                    }}
                  />
                </Box>

                {/* Title */}
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    color: '#1976d2',
                  }}
                >
                  Talk to a member of our Sales team
                </Typography>

                {/* Description */}
                <Typography
                  variant="body1"
                  sx={{
                    color: 'rgba(44, 62, 80, 0.7)',
                    mb: 4,
                    lineHeight: 1.6,
                  }}
                >
                  We'll help you find the right products and pricing for your business.
                </Typography>

                {/* Button */}
                <Button
                  variant="contained"
                  onClick={() => {
                    // TODO: Add contact sales functionality
                    window.location.href = 'mailto:sales@novamatch.com?subject=Sales Inquiry';
                  }}
                  sx={{
                    bgcolor: '#1976d2',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '1rem',
                    '&:hover': {
                      bgcolor: '#1565c0',
                    },
                  }}
                >
                  Contact Sales
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Support Card */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: '100%',
                borderRadius: 3,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                },
              }}
            >
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                {/* Icon */}
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: '#FFB6C1',
                    border: '3px solid #1976d2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  <HelpOutline
                    sx={{
                      fontSize: 40,
                      color: '#1976d2',
                    }}
                  />
                </Box>

                {/* Title */}
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    color: '#1976d2',
                  }}
                >
                  Product and account support
                </Typography>

                {/* Description */}
                <Typography
                  variant="body1"
                  sx={{
                    color: 'rgba(44, 62, 80, 0.7)',
                    mb: 4,
                    lineHeight: 1.6,
                  }}
                >
                  Our help center is fresh and always open for business. If you can't find the answer you're looking for, we're here to lend a hand.
                </Typography>

                {/* Button */}
                <Button
                  variant="contained"
                  onClick={() => {
                    // TODO: Add help center link
                    navigate('/');
                  }}
                  sx={{
                    bgcolor: '#1976d2',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '1rem',
                    '&:hover': {
                      bgcolor: '#1565c0',
                    },
                  }}
                >
                  Go to the help center
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Additional Support Information */}
        <Box sx={{ mt: 8, textAlign: 'center' }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              mb: 3,
              color: '#2c3e50',
            }}
          >
            Need more help?
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(44, 62, 80, 0.7)',
              mb: 2,
            }}
          >
            Email us at{' '}
            <Box
              component="a"
              href="mailto:support@novamatch.com"
              sx={{
                color: '#1976d2',
                textDecoration: 'none',
                fontWeight: 600,
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              support@novamatch.com
            </Box>
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(44, 62, 80, 0.6)',
            }}
          >
            We typically respond within 24 hours
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Support;
